import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, SlidersHorizontal } from "lucide-react";
import api from "@/lib/api";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ProductCard from "@/components/ProductCard";
import FilterPanel from "@/components/FilterPanel";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const CatalogPage = () => {
  const [searchParams] = useSearchParams();
  const [taxonomy, setTaxonomy] = useState({ lines: [], tags: [] });
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState(searchParams.get("search") || "");
  const [selectedTags, setSelectedTags] = useState([]);
  const [category, setCategory] = useState(searchParams.get("category") || "");
  const [sort, setSort] = useState("popular");

  useEffect(() => {
    api.get("/taxonomy").then(({ data }) => setTaxonomy(data));
  }, []);

  const fetchProducts = useCallback(() => {
    setLoading(true);
    api
      .get("/products", {
        params: {
          search: search || undefined,
          category: category || undefined,
          tags: selectedTags.length ? selectedTags.join(",") : undefined,
          sort,
        },
      })
      .then(({ data }) => setProducts(data))
      .finally(() => setLoading(false));
  }, [search, category, selectedTags, sort]);

  useEffect(() => {
    const t = setTimeout(fetchProducts, 250);
    return () => clearTimeout(t);
  }, [fetchProducts]);

  const toggleTag = (tag) => setSelectedTags((prev) => (prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]));
  const clearFilters = () => { setSelectedTags([]); setCategory(""); };

  const panel = (
    <FilterPanel
      tags={taxonomy.tags}
      lines={taxonomy.lines}
      selectedTags={selectedTags}
      onToggleTag={toggleTag}
      selectedCategory={category}
      onCategoryChange={setCategory}
      onClear={clearFilters}
    />
  );

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Header />
      <div className="px-4 sm:px-8 lg:px-12 max-w-7xl mx-auto py-10">
        <div className="mb-8">
          <span className="font-mono-tech text-xs uppercase tracking-widest text-emerald-400">Catálogo completo</span>
          <h1 className="mt-2 font-display text-3xl sm:text-4xl font-extrabold tracking-tight">Encuentra tu producto limpio</h1>
        </div>

        {/* Search + sort bar */}
        <div className="flex flex-col sm:flex-row gap-3 mb-8">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-3.5 h-4 w-4 text-muted-foreground" />
            <input
              data-testid="catalog-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por nombre, marca o ingrediente…"
              className="h-12 w-full rounded-full border border-border bg-secondary/40 pl-11 pr-4 text-sm outline-none focus:border-emerald-500/60"
            />
          </div>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger data-testid="sort-select" className="h-12 w-full sm:w-52 rounded-full bg-secondary/40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="popular">Más populares</SelectItem>
              <SelectItem value="purity">Mayor pureza</SelectItem>
              <SelectItem value="name">Nombre (A-Z)</SelectItem>
            </SelectContent>
          </Select>
          <Sheet>
            <SheetTrigger asChild>
              <Button data-testid="mobile-filter-btn" variant="outline" className="lg:hidden h-12 rounded-full border-border">
                <SlidersHorizontal className="mr-2 h-4 w-4" /> Filtros
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="bg-background border-border overflow-y-auto w-80">
              <h2 className="font-display font-bold mb-6">Filtros</h2>
              {panel}
            </SheetContent>
          </Sheet>
        </div>

        <div className="grid lg:grid-cols-[260px_1fr] gap-8">
          <aside className="hidden lg:block">
            <div className="sticky top-24 rounded-2xl border border-border bg-card p-6">{panel}</div>
          </aside>

          <div>
            <div className="flex items-center justify-between mb-5">
              <p data-testid="results-count" className="text-sm text-muted-foreground">
                {loading ? "Buscando…" : `${products.length} producto${products.length === 1 ? "" : "s"} encontrado${products.length === 1 ? "" : "s"}`}
              </p>
              {selectedTags.length > 0 && (
                <div className="hidden sm:flex flex-wrap gap-1.5">
                  {selectedTags.map((t) => (
                    <span key={t} className="rounded-full bg-emerald-500/15 border border-emerald-500/40 px-2 py-0.5 text-[11px] text-emerald-300">{t}</span>
                  ))}
                </div>
              )}
            </div>

            {loading ? (
              <p className="text-center text-muted-foreground py-20 font-mono-tech text-sm animate-pulse">Cargando…</p>
            ) : products.length === 0 ? (
              <div data-testid="empty-state" className="text-center py-20 rounded-2xl border border-dashed border-border">
                <p className="text-muted-foreground">No encontramos productos con esos filtros.</p>
                <button onClick={clearFilters} className="mt-3 text-sm text-emerald-400 hover:underline">Limpiar filtros</button>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
                {products.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
              </div>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default CatalogPage;
